import { useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Download, Package, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { exportInvProducts } from "~/services/inventory/exportService";
import {
  INV_CATEGORY_OPTIONS,
  INV_TRACKING_LABELS,
  InvProductCategory,
  InvProductMaster,
  inventoryProductService,
} from "~/services/inventory/productService";
import { formatCurrency } from "~/utils/helpers";

import { NewProductDialog } from "./components/NewProductDialog";

const formatQty = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n ?? 0);

export function InventoryProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [savingId, setSavingId] = useState<string | null>(null);

  const refetch = () => queryClient.invalidateQueries({ queryKey: ["inv-product-master"] });

  const { data, isLoading } = useQuery({
    queryKey: ["inv-product-master", search, category],
    queryFn: () =>
      inventoryProductService.getMaster({
        search,
        category: category === "all" ? null : (category as InvProductCategory),
      }),
  });
  const rows = data ?? [];

  const changeCategory = async (row: InvProductMaster, value: InvProductCategory) => {
    setSavingId(row.id);
    try {
      await inventoryProductService.setCategory(row.id, value);
      toast({ title: "Categoría actualizada", description: row.name });
      queryClient.invalidateQueries({ queryKey: ["inv-product-master"] });
    } catch (e) {
      toast({
        title: "No se pudo actualizar la categoría",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. Datos
          maestros de cada producto. La categoría es editable (clasificación inicial estimada).
        </span>
      </div>

      <PageHeader
        title="Inventario — Productos (Lista maestra)"
        description="Datos maestros de cada producto, categorizados por tipo."
        actions={
          <>
            <Button
              variant="outline"
              className="h-9"
              disabled={rows.length === 0}
              onClick={() => exportInvProducts(rows)}
            >
              <Download className="mr-1.5 h-4 w-4" />
              Exportar Excel
            </Button>
            <NewProductDialog onSuccess={refetch} />
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre…"
            className="h-9 pl-9"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {INV_CATEGORY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton rows={10} columns={8} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Package}
          title="Sin productos"
          description="No hay productos que coincidan con el filtro."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">Imagen</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead className="w-56">Categoría</TableHead>
                    <TableHead>UM</TableHead>
                    <TableHead>Seguimiento</TableHead>
                    <TableHead className="text-right">Costo unitario</TableHead>
                    <TableHead className="text-right">Último precio venta</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/inventario/productos/${r.id}`)}
                    >
                      <TableCell>
                        {r.image_url ? (
                          <img
                            src={r.image_url}
                            alt={r.name}
                            className="h-10 w-10 rounded border object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted/40 text-muted-foreground">
                            <Package className="h-4 w-4" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.code ?? "-"}
                      </TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        {r.company_name ? (
                          <Badge
                            variant={/pruebas/i.test(r.company_name) ? "secondary" : "outline"}
                            className="font-normal"
                          >
                            {r.company_name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={r.category ?? undefined}
                          onValueChange={(v) => changeCategory(r, v as InvProductCategory)}
                          disabled={savingId === r.id}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Sin categoría" />
                          </SelectTrigger>
                          <SelectContent>
                            {INV_CATEGORY_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{r.stock_uom}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {INV_TRACKING_LABELS[r.tracking] ?? r.tracking}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.standard_cost == null ? "-" : `$${formatCurrency(r.standard_cost)}`}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.last_sale_price == null ? "-" : `$${formatCurrency(r.last_sale_price)}`}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQty(r.total_stock)}
                      </TableCell>
                      <TableCell>
                        {r.is_active ? (
                          <Badge variant="secondary" className="font-normal">
                            Activo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="font-normal text-muted-foreground">
                            Inactivo
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default InventoryProductsPage;
